/**
 * runtime.ts — THE audio runtime (2026-09-20): one AudioContext, one mix, one
 * loader, one listener, one voice budget.
 *
 * Atlas had six independent sound owners — the sfx bus, the ambience module,
 * the music module, the engine AudioManager behind the zones, the world-script
 * tones and proximity voice — each with its own context, sharing nothing but a
 * mute switch. There was therefore no master volume, no ducking, no limiter, no
 * voice cap, four private caches, and a Web Audio node cannot be connected
 * across contexts, so none of that could be added without this. Owners are now
 * CLIENTS of this runtime; `lib/sfx.ts` keeps its old signatures as a shim.
 *
 *   source → voice gain → [air low-pass → obstruction → panner] → BUS
 *   BUS:  input → user volume → duck ─┬→ (world buses) world low-pass ─┐
 *                                     ├→ (weather) shelter low-pass ───┤
 *                                     └→ reverb send → convolver → wet ┤
 *                                                            GAME SUM ←┘
 *   GAME SUM → [night-mode compressor] → GAME GATE (the intro's mute) ─┐
 *   voice chat bus ────────────────────────────────────────────────────┤ (around the gate)
 *   cinematic bus ─────────────────────────────────────────────────────┤ (around the gate)
 *                        PRE-MASTER → overload guard → MASTER volume → out
 *
 * The master sits AFTER the guard: it can only attenuate, never push a guarded
 * signal back over unity. The guard is a DynamicsCompressorNode — an overload
 * guard, NOT a certified true-peak limiter (the spec promises no oversampled
 * ceiling); headroom comes from gain staging and the voice budget first.
 *
 * `AudioGraph` builds over any BaseAudioContext, so a probe can render the real
 * mix through an OfflineAudioContext and assert on the SAMPLES — the first gate
 * in this project that checks sound rather than state.
 *
 * Everything positional is LISTENER-RELATIVE (lib/audio/spatial_math.ts): the
 * native AudioListener never moves, on any browser.
 */
import { registerAudioMute, isGameAudioMuted, MUTE_RAMP_S } from '../audio_mute.js';
import {
  BUSES, BUS_SPEC, CRITICAL_KEYS, VOLUME_DEFAULT, VOLUME_STORAGE, budgetFor, classifyKey,
  type AudioBudget, type Bus, type OutputMode, type VolumeGroup,
} from './buses.js';
import { VoiceAllocator, STEAL_FADE_S } from './voices.js';
import { AudioAssetStore, type FetchResult } from './asset_store.js';
import {
  IDENTITY_POSE, airAbsorptionHz, bearingLabel, clampNearField, obstructionTarget, rotateByInverse, toListenerSpace,
  type ListeningPose, type Vec3,
} from './spatial_math.js';
import {
  DUCK_ATTACK_S, DUCK_RELEASE_S, REVERB_RECIPES, duckGains, envSnapshot, volumeToGain,
  type DuckProfile, type DuckRequest, type EnvInput, type EnvSnapshot, type ReverbKind,
} from './mix_math.js';
import { VariantPicker, parseVariantManifest, variantKey } from './variants.js';
import { proceduralFootSurfaceFor, proceduralMaterialFor, synthClick, synthFootstep, synthImpact, synthImpulseResponse } from './synth.js';

// ─── The graph (any BaseAudioContext) ────────────────────────────────────────

interface BusNodes { input: GainNode; user: GainNode; duck: GainNode; send: GainNode | null }

export class AudioGraph {
  readonly buses = {} as Record<Bus, BusNodes>;
  readonly worldSum: GainNode;
  readonly worldLP: BiquadFilterNode;
  readonly weatherLP: BiquadFilterNode;
  readonly gameSum: GainNode;
  readonly gameGate: GainNode;
  readonly preMaster: GainNode;
  readonly guard: DynamicsCompressorNode;
  /** Undoes the compressor's AUTOMATIC makeup gain (see `calibrateGuard`) — the guard must be unity below its threshold. */
  readonly guardTrim: GainNode;
  readonly master: GainNode;
  readonly reverbIn: GainNode;
  readonly reverbWet: GainNode;
  convolver: ConvolverNode | null = null;
  private nightComp: DynamicsCompressorNode | null = null;
  private nightMakeup: GainNode | null = null;

  constructor(readonly ctx: BaseAudioContext) {
    const gain = (v = 1): GainNode => { const g = ctx.createGain(); g.gain.value = v; return g; };
    const lowpass = (): BiquadFilterNode => {
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = Math.min(20000, ctx.sampleRate * 0.45);
      f.Q.value = 20 * Math.log10(Math.SQRT1_2); // a low-pass Q is in dB — Butterworth, no resonant bump
      return f;
    };
    this.worldSum = gain(); this.worldLP = lowpass(); this.weatherLP = lowpass();
    this.gameSum = gain(); this.gameGate = gain(); this.preMaster = gain();
    this.reverbIn = gain(); this.reverbWet = gain(0);
    this.guard = ctx.createDynamicsCompressor();
    this.guard.threshold.value = GUARD.threshold; this.guard.knee.value = GUARD.knee; this.guard.ratio.value = GUARD.ratio;
    this.guard.attack.value = GUARD.attack; this.guard.release.value = GUARD.release;
    this.guardTrim = gain(GUARD_TRIM_ESTIMATE);
    this.master = gain();

    this.worldSum.connect(this.worldLP).connect(this.gameSum);
    this.weatherLP.connect(this.gameSum);
    this.reverbWet.connect(this.gameSum);
    this.gameSum.connect(this.gameGate);
    this.gameGate.connect(this.preMaster);
    this.preMaster.connect(this.guard);
    this.guard.connect(this.guardTrim).connect(this.master);
    this.master.connect(ctx.destination);

    for (const bus of BUSES) {
      const spec = BUS_SPEC[bus];
      const n: BusNodes = { input: gain(spec.trim), user: gain(), duck: gain(), send: null };
      n.input.connect(n.user).connect(n.duck);
      if (spec.route !== 'game') n.duck.connect(this.preMaster); // voice chat + cinematics go AROUND the game gate, never around the master
      else if (spec.world) n.duck.connect(this.worldSum);
      else if (bus === 'weather') n.duck.connect(this.weatherLP);
      else n.duck.connect(this.gameSum);
      if (spec.reverbSend > 0) {
        // The send leaves AFTER the user volume and the duck: a category turned down must not stay audible through the room.
        n.send = gain(spec.reverbSend);
        n.duck.connect(n.send).connect(this.reverbIn);
      }
      this.buses[bus] = n;
    }
  }

  /** Swap the room (or remove it). The caller has already faded `reverbWet` to zero — a live convolver's buffer is never replaced under sound. */
  setImpulse(ir: AudioBuffer | null): void {
    if (this.convolver) { try { this.reverbIn.disconnect(this.convolver); this.convolver.disconnect(); } catch { /* gone */ } this.convolver = null; }
    if (!ir) return;
    const c = this.ctx.createConvolver();
    c.normalize = false; // the IR is energy-normalised by synth.ts; set BEFORE the buffer (the spec applies it at assignment)
    c.buffer = ir;
    this.reverbIn.connect(c).connect(this.reverbWet);
    this.convolver = c;
  }

  /** Reduced dynamic range ("night mode"): quiet detail up, peaks down — inserted only when asked (a compressor costs lookahead latency). */
  setNightMode(on: boolean): void {
    try { this.gameSum.disconnect(); } catch { /* fine */ }
    if (on) {
      if (!this.nightComp) {
        this.nightComp = this.ctx.createDynamicsCompressor();
        this.nightComp.threshold.value = -30; this.nightComp.knee.value = 18; this.nightComp.ratio.value = 3.5;
        this.nightComp.attack.value = 0.02; this.nightComp.release.value = 0.35;
        this.nightMakeup = this.ctx.createGain();
        // The node's own automatic makeup IS the "quiet detail up" half of night mode — this only leaves room for the guard.
        this.nightMakeup.gain.value = 0.9;
        this.nightComp.connect(this.nightMakeup);
      }
      this.gameSum.connect(this.nightComp);
      try { this.nightMakeup!.disconnect(); } catch { /* fine */ }
      this.nightMakeup!.connect(this.gameGate);
    } else {
      this.gameSum.connect(this.gameGate);
    }
  }

  setMono(on: boolean): void {
    this.preMaster.channelCount = on ? 1 : 2;
    this.preMaster.channelCountMode = 'explicit';
    this.preMaster.channelInterpretation = 'speakers';
  }
}

/** The guard's settings, shared with its calibration twin. */
const GUARD = { threshold: -6, knee: 6, ratio: 12, attack: 0.003, release: 0.25 };
/** Until the calibration lands (a few ms): roughly Chrome's makeup for those settings, erring QUIET. */
const GUARD_TRIM_ESTIMATE = 0.72;

/**
 * THE COMPRESSOR'S HIDDEN GAIN. A DynamicsCompressorNode applies automatic
 * MAKEUP gain derived from threshold / knee / ratio (the spec: the inverse of
 * the curve's full-range gain, to the power 0.6) — and the knee's shape is
 * user-agent-defined, so it cannot be computed portably. Left alone, an
 * "overload guard" makes the whole game ~3 dB louder at EVERY level. So it is
 * MEASURED: a −40 dBFS tone (far under the threshold) through an identical
 * compressor in an OfflineAudioContext; the ratio out/in is the makeup, and the
 * trim is its inverse. Resolves 1 where no offline context exists (node).
 */
export async function measureGuardMakeup(sampleRate: number): Promise<number> {
  const Off = (globalThis as unknown as { OfflineAudioContext?: typeof OfflineAudioContext }).OfflineAudioContext;
  if (!Off) return 1;
  try {
    const o = new Off(1, Math.round(sampleRate * 0.4), sampleRate);
    const osc = o.createOscillator();
    osc.frequency.value = 1000;
    const g = o.createGain();
    g.gain.value = 0.01;
    const c = o.createDynamicsCompressor();
    c.threshold.value = GUARD.threshold; c.knee.value = GUARD.knee; c.ratio.value = GUARD.ratio; c.attack.value = GUARD.attack; c.release.value = GUARD.release;
    osc.connect(g).connect(c).connect(o.destination);
    osc.start();
    const buf = await o.startRendering();
    const d = buf.getChannelData(0);
    let sum = 0, n = 0;
    for (let i = Math.floor(d.length * 0.5); i < d.length; i++) { sum += d[i] * d[i]; n++; }
    const makeup = Math.sqrt(sum / Math.max(1, n)) / (0.01 / Math.SQRT2);
    return Number.isFinite(makeup) && makeup > 0.5 && makeup < 4 ? makeup : 1;
  } catch { return 1; }
}

// ─── Public types ────────────────────────────────────────────────────────────

export interface PlayOpts {
  gain?: number;
  rate?: number;
  bus?: Bus;
  priority?: number;
  /** ABSOLUTE world position → a positional voice (listener-relative panner). */
  pos?: { x: number; y: number; z: number };
  /**
   * WHO OWNS DISTANCE. 'caller' (default): the call site already scaled `gain`
   * by range — the panner supplies DIRECTION only (rolloff 0). 'curve': the
   * panner attenuates (inverse, `refDistance` / `rolloff`). Never both: two
   * curves multiplied is the classic spatial-migration bug.
   */
  distance?: 'caller' | 'curve';
  refDistance?: number;
  rolloff?: number;
  /** Start this many seconds from now, on the AUDIO clock. */
  delayS?: number;
  /** Owner scope (`stopScope`). */
  scope?: string;
  /** A caption for players who cannot hear it (emitted with a bearing when positional). */
  caption?: string;
  /** false = never test this voice for obstruction (thunder, a sky-wide din: a wall beside you does not block the sky). */
  occlude?: boolean;
}

export interface LoopHandle {
  /** Drive the loop: gain 0..1 (0 = idle), playbackRate, ramp seconds. Cheap to call every frame. */
  set(gain: number, rate?: number, rampS?: number): void;
  stop(rampS?: number): void;
  /** Make the loop positional at an ABSOLUTE world point (call every frame for a mover); null = back to 2D. */
  at(pos: { x: number; y: number; z: number } | null): void;
}

export interface VoiceHandle { stop(fadeS?: number): void; readonly ended: boolean }

interface RtVoice {
  id: number; key: string; bus: Bus; scope: string; priority: number;
  src: AudioBufferSourceNode; gain: GainNode;
  lp: BiquadFilterNode | null; occ: GainNode | null; panner: PannerNode | null;
  pos: Vec3 | null; hrtf: boolean; loop: boolean;
  blocked: number; nextRayAt: number; rayEveryS: number; occlude: boolean;
  ended: boolean; unpin: () => void; epoch: number;
}

/** How a loop differs from the key's defaults: a catalog asset, a bus, a panner that OWNS distance (zones, speakers). */
export interface LoopOpts { assetId?: string; bus?: Bus; priority?: number; distance?: 'caller' | 'curve'; refDistance?: number; rolloff?: number; occlude?: boolean }

interface RtLoop {
  scope: string; key: string; handle: LoopHandle; voice: RtVoice | null; opts: LoopOpts; assetId: string;
  wantGain: number; wantRate: number; pos: Vec3 | null; silentSince: number; spatialBuilt: boolean;
}

/** A blocked direct path between two ABSOLUTE points? null = unknown (no physics yet). */
export type ObstructionProbe = (ax: number, ay: number, az: number, bx: number, by: number, bz: number) => boolean | null;

const LATE_MAX_MS = 180;
/** A direct-path ray is only meaningful this close (see sampleObstruction). */
const OCCLUSION_MAX_M = 120;
/** A loop idle this long is VIRTUAL: its source is released; it restarts when it is wanted again. */
const LOOP_VIRTUALIZE_S = 2;
const ASSET_VERSIONS: Array<[RegExp, string]> = [[/^(ambient_|versona_murmur)/, '2026-08-21d']]; // THE CACHE-BUST LAW: bump on every bed regen

// ─── The runtime ─────────────────────────────────────────────────────────────

export class AtlasAudioRuntime {
  ctx: AudioContext | null = null;
  graph: AudioGraph | null = null;
  private budget: AudioBudget = budgetFor('med');
  readonly allocator = new VoiceAllocator(this.budget);
  readonly store: AudioAssetStore<AudioBuffer>;
  private readonly voices = new Map<number, RtVoice>();
  private readonly loops = new Map<string, RtLoop>();
  private readonly procedural = new Map<string, AudioBuffer>();
  private readonly irCache = new Map<string, AudioBuffer>();
  private pose: ListeningPose = { ...IDENTITY_POSE, q: [0, 0, 0, 1] };
  private readonly ducks = new Map<string, DuckRequest>();
  private duckApplied: Partial<Record<Bus, number>> = {};
  private env: EnvSnapshot = envSnapshot({ submerged: false, indoors: false, inCave: false, spaceFlight: false, aboardShip: false });
  private reverbKind: ReverbKind = 'none';
  private reverbSwapTimer: ReturnType<typeof setTimeout> | null = null;
  private probe: ObstructionProbe | null = null;
  private rayTokens = 0;
  private hrtfInUse = 0;
  private epoch = 1;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private readonly volumes = {} as Record<VolumeGroup, number>;
  private muted = false;
  private output: OutputMode = 'headphones';
  private nightMode = false;
  private reverbEnabled = true;
  private captionsOn = false;
  private readonly tmp: Vec3 = [0, 0, 0];
  /** The guard's measured automatic makeup gain (1 = not measured / none). */
  guardMakeup = 0;
  private readonly listeners = new Set<() => void>();
  private captionSink: ((text: string, bearing: string | null) => void) | null = null;
  private lastCaptionAt = new Map<string, number>();
  readonly stats = { played: 0, droppedSuspended: 0, droppedLate: 0, latePlays: 0, procedural: 0, variantPlays: 0, rays: 0, raysBlocked: 0, resumes: 0, captions: 0, unlockedAtMs: 0 };

  constructor() {
    this.store = new AudioAssetStore<AudioBuffer>({
      resolve: (id) => this.resolveUrl(id),
      fetchBytes: (url) => this.fetchBytes(url),
      decode: (bytes) => (this.ctx ? this.ctx.decodeAudioData(bytes) : Promise.reject(new Error('no context'))),
      sizeOf: (b) => b.length * b.numberOfChannels * 4,
      now: () => performance.now(),
      sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
      rng: () => Math.random(),
    }, this.budget.decodedMiB);
    this.loadSettings();
  }

  // ── settings ──
  private loadSettings(): void {
    const read = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
    for (const g of Object.keys(VOLUME_DEFAULT) as VolumeGroup[]) {
      const raw = read(VOLUME_STORAGE[g]);
      const v = raw === null ? NaN : Number(raw);
      this.volumes[g] = Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : VOLUME_DEFAULT[g];
    }
    this.muted = read('atlas:audio:muted') === '1';
    const o = read('atlas:audio:output');
    this.output = o === 'speakers' || o === 'mono' || o === 'headphones' ? o : 'headphones';
    this.nightMode = read('atlas:audio:range') === 'night';
    this.reverbEnabled = read('atlas:audio:reverb') !== 'off';
    this.captionsOn = read('atlas:audio:captions') === 'on';
  }
  private store_(k: string, v: string): void { try { localStorage.setItem(k, v); } catch { /* private mode */ } }

  volume(g: VolumeGroup): number { return this.volumes[g]; }
  setVolume(g: VolumeGroup, v: number): void {
    const c = Math.min(1, Math.max(0, v));
    this.volumes[g] = c;
    this.store_(VOLUME_STORAGE[g], String(c));
    this.applyVolumes(0.05);
    this.emitChange();
  }
  isMuted(): boolean { return this.muted; }
  setMuted(m: boolean): void { this.muted = m; this.store_('atlas:audio:muted', m ? '1' : '0'); this.applyVolumes(0.05); this.emitChange(); }
  outputMode(): OutputMode { return this.output; }
  setOutputMode(m: OutputMode): void { this.output = m; this.store_('atlas:audio:output', m); this.graph?.setMono(m === 'mono'); this.emitChange(); }
  isNightMode(): boolean { return this.nightMode; }
  setNightMode(on: boolean): void { this.nightMode = on; this.store_('atlas:audio:range', on ? 'night' : 'full'); this.graph?.setNightMode(on); this.emitChange(); }
  isReverbEnabled(): boolean { return this.reverbEnabled; }
  setReverbEnabled(on: boolean): void { this.reverbEnabled = on; this.store_('atlas:audio:reverb', on ? 'on' : 'off'); this.applyEnvironment(true); this.emitChange(); }
  captionsEnabled(): boolean { return this.captionsOn; }
  setCaptionsEnabled(on: boolean): void { this.captionsOn = on; this.store_('atlas:audio:captions', on ? 'on' : 'off'); this.emitChange(); }
  /** Things that mirror the master outside Web Audio (the intro's media elements) listen here. */
  onChange(fn: () => void): () => void { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; }
  private emitChange(): void { for (const fn of this.listeners) { try { fn(); } catch { /* a listener mid-teardown */ } } }
  /** The linear gain a NON-WebAudio element of a group should use (master × group × mute) — the intro's <audio>/<video>. */
  elementGain(g: VolumeGroup): number { return this.muted ? 0 : volumeToGain(this.volumes.master) * volumeToGain(this.volumes[g]); }

  private applyVolumes(rampS: number): void {
    const gr = this.graph;
    if (!gr) return;
    const t = gr.ctx.currentTime;
    const ramp = (p: AudioParam, v: number): void => { p.cancelScheduledValues(t); p.setValueAtTime(p.value, t); p.linearRampToValueAtTime(v, t + rampS); };
    ramp(gr.master.gain, this.muted ? 0 : volumeToGain(this.volumes.master));
    for (const bus of BUSES) {
      const group = BUS_SPEC[bus].group;
      // Dialogue keeps its older control (versona_tts owns a gain off `atlas:voices-volume`) — never applied twice.
      ramp(gr.buses[bus].user.gain, group === 'dialogue' ? 1 : volumeToGain(this.volumes[group]));
    }
  }

  setTier(tier: string): void {
    this.budget = budgetFor(tier);
    this.allocator.setBudget(this.budget);
    this.store.setBudgetMiB(this.budget.decodedMiB);
    this.applyEnvironment(true);
  }

  // ── lifecycle ──
  /**
   * Create / resume the context. MUST be reachable from a user gesture: every
   * pointerdown and keydown calls it (cheap when running), which is also the
   * recovery path after an iOS interruption or a backgrounded tab.
   */
  unlock(): void {
    if (typeof window === 'undefined') return;
    if (!this.ctx) {
      try {
        const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return;
        this.ctx = new Ctor({ latencyHint: 'interactive' });
      } catch { this.ctx = null; return; } // no audio device — the game runs silent
      this.graph = new AudioGraph(this.ctx);
      this.graph.setMono(this.output === 'mono');
      if (this.nightMode) this.graph.setNightMode(true);
      this.applyVolumes(0);
      // ONE applier for the whole game mix (the intro's switch). Born muted if the very click that made us began the intro.
      const gate = this.graph.gameGate;
      const ctx = this.ctx;
      gate.gain.value = isGameAudioMuted() ? 0 : 1;
      registerAudioMute('runtime', (muted) => {
        try { const t = ctx.currentTime; gate.gain.cancelScheduledValues(t); gate.gain.setValueAtTime(gate.gain.value, t); gate.gain.linearRampToValueAtTime(muted ? 0 : 1, t + MUTE_RAMP_S); }
        catch { gate.gain.value = muted ? 0 : 1; }
      });
      ctx.addEventListener('statechange', () => this.emitChange());
      // Unity below the threshold: undo the compressor's automatic makeup gain, by measurement.
      const graph = this.graph;
      void measureGuardMakeup(ctx.sampleRate).then((makeup) => { this.guardMakeup = makeup; graph.guardTrim.gain.value = 1 / makeup; });
      this.stats.unlockedAtMs = Math.round(performance.now());
      void this.store.preload(CRITICAL_KEYS.map((k) => `a:${k}`)); // THE FIRST-PLAY MISS, closed
      void this.loadVariants();
      this.applyEnvironment(true);
      this.emitChange();
    }
    if (this.ctx.state !== 'running') { this.stats.resumes++; this.ctx.resume().catch(() => { /* still blocked — the next gesture tries again */ }); }
  }
  get running(): boolean { return this.ctx?.state === 'running'; }

  /** A bus's input — for owners that build their own sources (music, TTS, voice chat, script tones). */
  busInput(bus: Bus): GainNode | null { return this.graph?.buses[bus].input ?? null; }

  // ── assets ──
  private resolveUrl(id: string): string {
    const kind = id.slice(0, 2), key = id.slice(2);
    if (kind === 'c:') return `${this.catalogBase}/${key}`; // a render_asset key lives in the ASSET STORE, never beside the page
    if (kind === 'm:') return `music/${key}.mp3`;
    let v = '';
    for (const [re, ver] of ASSET_VERSIONS) if (re.test(key)) { v = `?v=${ver}`; break; }
    return `audio/${key}.mp3${v}`;
  }
  /** Set by the app (config.ASSET_STORE_BASE) — kept out of this file so the lib stays importable in node. */
  catalogBase = '';

  private async fetchBytes(url: string): Promise<FetchResult> {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 12_000);
    try {
      const res = await fetch(url, { signal: ctl.signal });
      const ra = Number(res.headers.get('retry-after'));
      if (!res.ok) return { status: res.status, bytes: null, retryAfterMs: Number.isFinite(ra) && ra > 0 ? ra * 1000 : undefined };
      return { status: res.status, bytes: await res.arrayBuffer() };
    } catch { return { status: 0, bytes: null }; } // network error / timeout → transient
    finally { clearTimeout(timer); }
  }

  available(key: string): boolean | null { return this.ctx ? (this.variants.has(key) ? true : this.store.available(`a:${key}`)) : null; }

  // ── variants (lib/audio/variants.ts): several takes of one logical key ──
  private variants = new Map<string, number>();
  private readonly picker = new VariantPicker();
  /** How many recorded takes a logical key has (0 = none: the single clip of that name, or a synthesised stand-in). */
  variantCount(key: string): number { return this.variants.get(key) ?? 0; }
  /** The generator's manifest. A 404 or a malformed file is simply "no variants" — the game sounds as it did before. */
  private async loadVariants(): Promise<void> {
    try {
      const r = await fetch('audio/variants.json', { cache: 'no-cache' });
      if (!r.ok) return;
      this.variants = parseVariantManifest(await r.json());
      // Takes are short one-shots by construction (a footfall, a strike): decode them all now, so the first stride is recorded too.
      const ids: string[] = [];
      for (const [key, n] of this.variants) for (let i = 1; i <= n; i++) ids.push(`a:${variantKey(key, i)}`);
      if (ids.length) void this.store.preload(ids);
      this.emitChange();
    } catch { /* offline, or not JSON: no variants */ }
  }
  preload(keys: readonly string[]): Promise<{ ready: string[]; failed: string[] }> {
    if (!this.ctx) return Promise.resolve({ ready: [], failed: [] });
    return this.store.preload(keys.map((k) => `a:${k}`));
  }

  private proceduralFor(key: string): AudioBuffer | null {
    const ctx = this.ctx;
    if (!ctx) return null;
    const material = proceduralMaterialFor(key);
    const foot = material ? '' : proceduralFootSurfaceFor(key);
    const isClick = key === 'ui_click';
    if (!material && !foot && !isClick) return null;
    // Three variants per material (the same wall must not ring identically thrice); SIX per footfall — a gait repeats
    // twice a second, and the ear finds a loop of three at once.
    const variant = material ? 1 + Math.floor(Math.random() * 3) : foot ? 1 + Math.floor(Math.random() * 6) : 0;
    const id = `${key}#${variant}`;
    let buf = this.procedural.get(id);
    if (!buf) {
      const pcm = material ? synthImpact(material, ctx.sampleRate, variant) : foot ? synthFootstep(foot, ctx.sampleRate, variant) : synthClick(ctx.sampleRate);
      buf = ctx.createBuffer(1, pcm.length, ctx.sampleRate);
      buf.copyToChannel(pcm, 0);
      this.procedural.set(id, buf);
    }
    return buf;
  }

  // ── one-shots ──
  /** Play a clip by key. Returns null when nothing will sound (context locked, budget, still loading past its deadline). */
  playKey(key: string, opts: PlayOpts = {}): VoiceHandle | null {
    if (!this.ctx || !this.graph) return null;
    if (this.ctx.state !== 'running') { this.stats.droppedSuspended++; return null; } // never bank one-shots on a suspended context — they would all fire on resume
    // Several recorded takes → the shuffle bag picks one. It plays under the LOGICAL key (its bus, its instance cap).
    const takes = this.variants.get(key) ?? 0;
    if (takes > 0) {
      const vid = `a:${variantKey(key, this.picker.pick(key, takes))}`;
      const take = this.store.get(vid);
      if (take) { this.stats.variantPlays++; return this.handleOf(this.start(take, key, opts, vid)); }
      // Still decoding (or evicted: `get` has started the reload): the stand-in, this once. A key WITH takes has no file
      // of its own name — never ask for `a:<key>` (a dev server answers a missing .mp3 with 200 + HTML, a decode failure).
      const stand = this.proceduralFor(key);
      if (stand) { this.stats.procedural++; return this.handleOf(this.start(stand, key, opts, null)); }
      return null;
    }
    const id = `a:${key}`;
    const buf = this.store.get(id);
    if (buf) return this.handleOf(this.start(buf, key, opts, id));
    // Not decoded. A semantic stand-in plays NOW (the five material impacts have no clip at all); otherwise the
    // event may arrive late — but only inside its deadline, and only in the world that asked for it.
    const stand = this.proceduralFor(key);
    if (stand) { this.stats.procedural++; return this.handleOf(this.start(stand, key, opts, null)); }
    if (this.store.state(id) === 'failed') return null;
    const asked = performance.now(), epoch = this.epoch;
    void this.store.load(id).then((b) => {
      if (!b || epoch !== this.epoch) return;
      if (performance.now() - asked > LATE_MAX_MS) { this.stats.droppedLate++; return; }
      this.stats.latePlays++;
      this.start(b, key, opts, id);
    });
    return null;
  }

  /** Play a decoded buffer an owner already holds (zone clips, TTS). */
  playBuffer(buf: AudioBuffer, key: string, opts: PlayOpts = {}): VoiceHandle | null {
    if (!this.ctx || !this.graph || this.ctx.state !== 'running') return null;
    return this.handleOf(this.start(buf, key, opts, null));
  }

  /**
   * The settings panel's speaker check: a crystal ping 2 m to the listener's LEFT, RIGHT or straight AHEAD, placed
   * through the very same listener-space transform every world sound takes — so it proves the whole chain (pose,
   * panner, buses, output mode), not just that a node can pan. In MONO it is rightly the same in both ears.
   */
  playTest(where: 'left' | 'right' | 'ahead'): boolean {
    if (!this.ctx || !this.graph) return false;
    this.unlock();
    if (this.ctx.state !== 'running') return false;
    const local: Vec3 = where === 'left' ? [-2, 0, -0.35] : where === 'right' ? [2, 0, -0.35] : [0, 0, -2];
    const q = this.pose.q;
    const w = rotateByInverse([0, 0, 0], local, [-q[0], -q[1], -q[2], q[3]]); // the conjugate's inverse = a FORWARD rotation: listener-local → world
    const buf = this.proceduralFor('sfx_impact_crystal');
    if (!buf) return false;
    const v = this.start(buf, 'ui_test', { gain: 0.7, bus: 'ui', priority: 99, pos: { x: this.pose.x + w[0], y: this.pose.y + w[1], z: this.pose.z + w[2] }, distance: 'caller', occlude: false }, null);
    return !!v;
  }

  private handleOf(v: RtVoice | null): VoiceHandle | null {
    if (!v) return null;
    const self = this;
    return { stop(fadeS = 0.05): void { self.fadeOut(v, fadeS); }, get ended(): boolean { return v.ended; } };
  }

  private start(buf: AudioBuffer, key: string, opts: PlayOpts, assetId: string | null, loop = false): RtVoice | null {
    const ctx = this.ctx, gr = this.graph;
    if (!ctx || !gr) return null;
    const cls = classifyKey(key);
    const bus = opts.bus ?? cls.bus;
    const priority = opts.priority ?? cls.priority;
    const rate = opts.rate ?? 1;
    const gainV = Math.max(0, opts.gain ?? 0.5);
    const now = ctx.currentTime;
    const req = { key, bus, priority, gain: Math.min(1, gainV), loop, durationS: buf.duration / Math.max(0.05, rate) + (opts.delayS ?? 0) };
    const verdict = this.allocator.admit(req, now);
    if (verdict.kind === 'drop') return null;
    let when = now + (opts.delayS ?? 0);
    if (verdict.kind === 'steal') {
      const victim = this.voices.get(verdict.victimId);
      this.allocator.markReleasing(verdict.victimId);
      if (victim) this.fadeOut(victim, STEAL_FADE_S);
      else this.allocator.release(verdict.victimId);
      when = Math.max(when, now + STEAL_FADE_S); // the newcomer takes the SAME slot, after the victim — no hidden +1
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = loop;
    src.playbackRate.value = rate;
    const g = ctx.createGain();
    g.gain.value = loop ? 0 : gainV;
    src.connect(g);
    const v: RtVoice = {
      id: this.allocator.add(req, now), key, bus, scope: opts.scope ?? '', priority, src, gain: g,
      lp: null, occ: null, panner: null, pos: null, hrtf: false, loop,
      blocked: 0, nextRayAt: 0, rayEveryS: priority >= 75 ? 0.1 : 0.25, occlude: opts.occlude !== false,
      ended: false, unpin: assetId ? this.store.pin(assetId) : () => { /* procedural */ }, epoch: this.epoch,
    };
    if (opts.pos) this.buildSpatial(v, opts, buf.duration);
    else g.connect(gr.buses[bus].input);
    src.onended = () => this.cleanup(v);
    try { src.start(when, loop ? Math.random() * Math.max(0, buf.duration - 0.05) : 0); } catch { this.cleanup(v); return null; }
    this.voices.set(v.id, v);
    this.stats.played++;
    if (opts.caption) this.caption(opts.caption, key, v.pos);
    return v;
  }

  private buildSpatial(v: RtVoice, opts: PlayOpts, durationS: number): void {
    const ctx = this.ctx!, gr = this.graph!;
    const p = opts.pos!;
    v.pos = [p.x, p.y, p.z];
    const panner = ctx.createPanner();
    // HRTF is a per-source convolution: spend it on what a player must LOCATE (threats, evidence, speech), inside the tier's cap.
    const wantHrtf = this.output === 'headphones' && v.priority >= 70 && this.hrtfInUse < this.budget.hrtf;
    panner.panningModel = wantHrtf ? 'HRTF' : 'equalpower';
    v.hrtf = wantHrtf;
    if (wantHrtf) this.hrtfInUse++;
    panner.distanceModel = 'inverse';
    if ((opts.distance ?? 'caller') === 'caller') { panner.refDistance = 1; panner.rolloffFactor = 0; } // DIRECTION only — the call site owns distance
    else { panner.refDistance = opts.refDistance ?? 4; panner.rolloffFactor = opts.rolloff ?? 1; }
    panner.coneInnerAngle = 360; panner.coneOuterAngle = 360;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.Q.value = 20 * Math.log10(Math.SQRT1_2);
    const occ = ctx.createGain();
    v.lp = lp; v.occ = occ; v.panner = panner;
    v.gain.connect(lp).connect(occ).connect(panner).connect(gr.buses[v.bus].input);
    this.placeVoice(v, true);
    // A shot that starts behind a wall starts MUFFLED: one ray now (if the budget has one), applied without a slew.
    if ((v.loop || durationS > 0.3) && v.priority >= 50) this.sampleObstruction(v, true);
  }

  /** Write a voice's listener-relative position, air absorption and obstruction. */
  private placeVoice(v: RtVoice, immediate: boolean): void {
    if (!v.pos || !v.panner || !v.lp || !this.ctx) return;
    const local = clampNearField(toListenerSpace(this.tmp, v.pos[0], v.pos[1], v.pos[2], this.pose));
    const t = this.ctx.currentTime;
    v.panner.positionX.setValueAtTime(local[0], t);
    v.panner.positionY.setValueAtTime(local[1], t);
    v.panner.positionZ.setValueAtTime(local[2], t);
    const dist = Math.hypot(local[0], local[1], local[2]);
    const ob = obstructionTarget(v.blocked);
    const cutoff = Math.min(airAbsorptionHz(dist), ob.cutoffHz, this.ctx.sampleRate * 0.45);
    if (immediate) { v.lp.frequency.setValueAtTime(cutoff, t); v.occ!.gain.setValueAtTime(ob.gain, t); }
    else { v.lp.frequency.setTargetAtTime(cutoff, t, 0.06); v.occ!.gain.setTargetAtTime(ob.gain, t, 0.06); }
  }

  private sampleObstruction(v: RtVoice, immediate: boolean): void {
    if (!this.probe || !v.pos || !v.occlude || this.rayTokens < 1) return;
    const dx = v.pos[0] - this.pose.x, dy = v.pos[1] - this.pose.y, dz = v.pos[2] - this.pose.z;
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 < 9) { v.blocked = 0; return; } // within 3 m nothing meaningful stands between
    // Beyond ~120 m a single ray proves nothing: sound bends round a wall over that distance, and physics only holds
    // colliders near the player anyway — a battle over the ridge must not go dull because you stand beside a shed.
    if (d2 > OCCLUSION_MAX_M * OCCLUSION_MAX_M) { v.blocked = 0; return; }
    this.rayTokens -= 1;
    this.stats.rays++;
    const hit = this.probe(this.pose.x, this.pose.y, this.pose.z, v.pos[0], v.pos[1], v.pos[2]);
    if (hit === null) return; // unknown: keep the last answer
    if (hit) this.stats.raysBlocked++;
    // A stable answer: move a fraction toward the sample (one ray flickers at a doorway; four agreeing samples do not).
    v.blocked = immediate ? (hit ? 1 : 0) : v.blocked + ((hit ? 1 : 0) - v.blocked) * 0.45;
  }

  private fadeOut(v: RtVoice, fadeS: number): void {
    const ctx = this.ctx;
    if (!ctx || v.ended) return;
    const t = ctx.currentTime;
    try {
      v.gain.gain.cancelScheduledValues(t);
      v.gain.gain.setValueAtTime(v.gain.gain.value, t);
      v.gain.gain.linearRampToValueAtTime(0, t + fadeS);
      v.src.stop(t + fadeS + 0.005);
    } catch { this.cleanup(v); }
  }

  private cleanup(v: RtVoice): void {
    if (v.ended) return;
    v.ended = true;
    try { v.src.onended = null; v.src.disconnect(); v.gain.disconnect(); v.lp?.disconnect(); v.occ?.disconnect(); v.panner?.disconnect(); } catch { /* torn down */ }
    if (v.hrtf) this.hrtfInUse = Math.max(0, this.hrtfInUse - 1);
    v.unpin();
    this.allocator.release(v.id);
    this.voices.delete(v.id);
  }

  // ── loops (scoped) ──
  /**
   * A persistent loop, ONE per (scope, key). The old bus keyed loops by clip
   * alone, so two systems sharing a clip fought over one source ("the last
   * set() wins") and one system's teardown stopped everyone's. A scope is an
   * owner: `stopScope('space')` ends the sector's loops and nothing else.
   */
  loop(scope: string, key: string, opts: LoopOpts = {}): LoopHandle {
    const id = `${scope}|${key}`;
    let l = this.loops.get(id);
    if (l) return l.handle;
    const self = this;
    l = {
      scope, key, voice: null, opts, assetId: opts.assetId ?? `a:${key}`, wantGain: 0, wantRate: 1, pos: null, silentSince: 0, spatialBuilt: false,
      handle: {
        set(gain: number, rate = 1, rampS = 0.15): void { self.driveLoop(id, gain, rate, rampS); },
        stop(rampS = 0.25): void { self.stopLoop(id, rampS); },
        at(pos): void { const ll = self.loops.get(id); if (ll) ll.pos = pos ? [pos.x, pos.y, pos.z] : null; },
      },
    };
    this.loops.set(id, l);
    return l.handle;
  }

  private driveLoop(id: string, gain: number, rate: number, rampS: number): void {
    const l = this.loops.get(id);
    const ctx = this.ctx;
    if (!l) return;
    l.wantGain = gain; l.wantRate = rate;
    if (!ctx || !this.graph) return;
    const now = ctx.currentTime;
    // The voice was stolen or torn down under us: forget it (a later set() restarts it).
    if (l.voice?.ended) l.voice = null;
    // 2D ↔ positional changed since the source was built: rebuild with a short overlap.
    if (l.voice && l.spatialBuilt !== (l.pos !== null)) { this.fadeOut(l.voice, 0.08); l.voice = null; }
    if (!l.voice) {
      if (gain <= 0.001) return; // stay virtual until someone wants it
      const buf = this.store.get(l.assetId);
      if (!buf) return; // decoding — a later set() starts it
      const v = this.start(buf, l.key, { ...l.opts, gain: 0, rate, scope: l.scope, pos: l.pos ? { x: l.pos[0], y: l.pos[1], z: l.pos[2] } : undefined }, l.assetId, true);
      if (!v) return; // the budget said no — ask again next tick
      l.voice = v;
      l.spatialBuilt = l.pos !== null;
      l.silentSince = 0;
    }
    const v = l.voice;
    if (l.pos && v.pos) { v.pos[0] = l.pos[0]; v.pos[1] = l.pos[1]; v.pos[2] = l.pos[2]; }
    v.src.playbackRate.setValueAtTime(rate, now);
    const gg = v.gain.gain;
    gg.cancelScheduledValues(now);
    gg.setValueAtTime(gg.value, now);
    gg.linearRampToValueAtTime(Math.max(0, gain), now + Math.max(0.005, rampS));
    this.allocator.setGain(v.id, Math.min(1, gain));
    // VIRTUALIZE: an idle loop holds no source, no slot and no CPU. (Every Atlas loop is a texture — it may restart anywhere.)
    if (gain <= 0.001) {
      if (l.silentSince === 0) l.silentSince = now;
      else if (now - l.silentSince > LOOP_VIRTUALIZE_S + rampS) { this.fadeOut(v, 0.02); l.voice = null; l.silentSince = 0; }
    } else l.silentSince = 0;
  }

  private stopLoop(id: string, rampS: number): void {
    const l = this.loops.get(id);
    if (!l) return;
    l.wantGain = 0;
    if (l.voice) { this.fadeOut(l.voice, rampS); l.voice = null; } // the fade owns the OLD voice by closure — a restart can never be cut by it
    l.silentSince = 0;
  }

  /** Stop AND forget one owner's loops (a deleted zone must not leave a handle behind for the session). */
  dropScope(scope: string, rampS = 0.3): void {
    for (const [id, l] of this.loops) if (l.scope === scope) { this.stopLoop(id, rampS); this.loops.delete(id); }
  }

  /** End everything an owner started (its loops fade; its one-shots finish unless `hard`). */
  stopScope(scope: string, rampS = 0.3, hard = false): void {
    for (const [id, l] of this.loops) if (l.scope === scope) this.stopLoop(id, rampS);
    if (hard) for (const v of [...this.voices.values()]) if (v.scope === scope) this.fadeOut(v, rampS);
  }
  /** The old global stop — kept for a real teardown, never for one system's exit. */
  stopAllLoops(rampS = 0.3): void { for (const id of this.loops.keys()) this.stopLoop(id, rampS); }

  // ── worlds + delayed playback ──
  /**
   * A delayed sound (thunder's 0.9–10.5 s, a second footstep, a respawn chime)
   * belongs to the WORLD it was scheduled in. Leaving that world, or the page
   * going to sleep past its moment, cancels it — an old world's thunder must
   * never clap in the new one.
   */
  later(ms: number, fn: () => void): void {
    const epoch = this.epoch;
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      if (epoch !== this.epoch) return;
      try { fn(); } catch { /* a caller mid-teardown */ }
    }, ms);
    this.timers.add(timer);
  }
  /** The world changed: nothing scheduled in the old one may sound in the new one. Decoded assets stay cached. */
  leaveWorld(): void {
    this.epoch++;
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    for (const v of [...this.voices.values()]) if (!v.loop && v.pos) this.fadeOut(v, 0.15); // positional one-shots were AT a place that no longer exists
  }
  get worldEpoch(): number { return this.epoch; }

  // ── the listener, the room, the mix: once per frame ──
  setListenerPose(x: number, y: number, z: number, qx: number, qy: number, qz: number, qw: number): void {
    const p = this.pose;
    if (!Number.isFinite(x + y + z + qx + qy + qz + qw)) return; // never NaN into an AudioParam
    p.x = x; p.y = y; p.z = z;
    const n = Math.hypot(qx, qy, qz, qw);
    if (n > 1e-9) { p.q[0] = qx / n; p.q[1] = qy / n; p.q[2] = qz / n; p.q[3] = qw / n; }
  }
  listenerPose(): ListeningPose { return this.pose; }
  /** An ABSOLUTE point in listener-local space (−Z ahead) — owners with their own panners (voice chat) use it. */
  toLocal(out: Vec3, x: number, y: number, z: number): Vec3 { return clampNearField(toListenerSpace(out, x, y, z, this.pose)); }

  setObstructionProbe(p: ObstructionProbe | null): void { this.probe = p; }
  setEnvironment(e: EnvInput): void {
    const next = envSnapshot(e);
    if (next.worldCutoffHz === this.env.worldCutoffHz && next.weatherCutoffHz === this.env.weatherCutoffHz && next.reverb === this.env.reverb && next.reverbWet === this.env.reverbWet) return;
    this.env = next;
    this.applyEnvironment(false);
  }

  private applyEnvironment(force: boolean): void {
    const gr = this.graph, ctx = this.ctx;
    if (!gr || !ctx) return;
    const t = ctx.currentTime, nyq = ctx.sampleRate * 0.45;
    gr.worldLP.frequency.setTargetAtTime(Math.min(nyq, this.env.worldCutoffHz), t, 0.18);
    gr.weatherLP.frequency.setTargetAtTime(Math.min(nyq, this.env.weatherCutoffHz), t, 0.35);
    const kind: ReverbKind = this.reverbEnabled && this.budget.convolvers > 0 ? this.env.reverb : 'none';
    if (kind === this.reverbKind && !force) return;
    this.reverbKind = kind;
    // Fade the wet out, THEN swap the room, THEN bring it up: a live convolver's buffer is never replaced under sound.
    gr.reverbWet.gain.cancelScheduledValues(t);
    gr.reverbWet.gain.setValueAtTime(gr.reverbWet.gain.value, t);
    gr.reverbWet.gain.linearRampToValueAtTime(0, t + 0.25);
    if (this.reverbSwapTimer) clearTimeout(this.reverbSwapTimer);
    this.reverbSwapTimer = setTimeout(() => {
      this.reverbSwapTimer = null;
      if (!this.graph || !this.ctx) return;
      if (this.reverbKind === 'none') { this.graph.setImpulse(null); return; }
      this.graph.setImpulse(this.impulse(this.reverbKind));
      const tt = this.ctx.currentTime;
      this.graph.reverbWet.gain.setValueAtTime(0, tt);
      this.graph.reverbWet.gain.linearRampToValueAtTime(this.env.reverbWet, tt + 0.6);
    }, 280);
  }

  private impulse(kind: Exclude<ReverbKind, 'none'>): AudioBuffer {
    let ir = this.irCache.get(kind);
    if (!ir) {
      const ctx = this.ctx!;
      const r = REVERB_RECIPES[kind];
      const [l, rr] = synthImpulseResponse(ctx.sampleRate, r.seconds, r.damping, r.preDelayMs, r.density);
      ir = ctx.createBuffer(2, l.length, ctx.sampleRate);
      ir.copyToChannel(l, 0);
      ir.copyToChannel(rr, 1);
      this.irCache.set(kind, ir);
    }
    return ir;
  }

  /**
   * Ask buses to make room until `untilCtxS` (audio clock). A TOKEN: asking
   * again with the same tag moves its end; `unduck(tag)` releases it early —
   * a cancelled utterance can never leave the music ducked.
   */
  duck(tag: string, profile: DuckProfile, untilCtxS: number): void { this.ducks.set(tag, { tag, profile, untilS: untilCtxS }); }
  unduck(tag: string): void { this.ducks.delete(tag); }

  /** Once per rendered frame (AtlasAudioRuntimeModule). */
  update(dt: number): void {
    const ctx = this.ctx, gr = this.graph;
    if (!ctx || !gr) return;
    const now = ctx.currentTime;
    this.rayTokens = Math.min(this.budget.raysPerSec, this.rayTokens + this.budget.raysPerSec * Math.min(0.1, Math.max(0, dt)));
    for (const v of this.voices.values()) {
      if (!v.pos || v.ended) continue;
      if (now >= v.nextRayAt && (v.loop || v.priority >= 50)) { v.nextRayAt = now + v.rayEveryS; this.sampleObstruction(v, false); }
      this.placeVoice(v, false);
    }
    // Ducking: the strongest live request per bus; attack fast, release slow.
    for (const [tag, r] of this.ducks) if (r.untilS <= now) this.ducks.delete(tag);
    const want = duckGains(this.ducks.values(), now);
    for (const bus of BUSES) {
      const target = want[bus] ?? 1;
      const cur = this.duckApplied[bus] ?? 1;
      if (Math.abs(target - cur) < 1e-3) continue;
      this.duckApplied[bus] = target;
      gr.buses[bus].duck.gain.setTargetAtTime(target, now, (target < cur ? DUCK_ATTACK_S : DUCK_RELEASE_S) / 3); // a time constant reaches ~95 % in three
    }
  }

  // ── captions ──
  setCaptionSink(sink: ((text: string, bearing: string | null) => void) | null): void { this.captionSink = sink; }
  private caption(text: string, key: string, pos: Vec3 | null): void {
    if (!this.captionsOn || !this.captionSink) return;
    const nowMs = performance.now();
    if (nowMs - (this.lastCaptionAt.get(key) ?? -1e9) < 1500) return;
    this.lastCaptionAt.set(key, nowMs);
    this.stats.captions++;
    this.captionSink(text, pos ? bearingLabel(toListenerSpace([0, 0, 0], pos[0], pos[1], pos[2], this.pose)) : null);
  }

  // ── diagnostics ──
  diag(): Record<string, unknown> {
    const byBus: Record<string, number> = {};
    let spatial = 0, hrtf = 0, blocked = 0;
    for (const v of this.voices.values()) { byBus[v.bus] = (byBus[v.bus] ?? 0) + 1; if (v.pos) { spatial++; if (v.hrtf) hrtf++; if (v.blocked > 0.5) blocked++; } }
    let loopsLive = 0;
    for (const l of this.loops.values()) if (l.voice && !l.voice.ended) loopsLive++;
    return {
      runtime: { state: this.ctx?.state ?? 'uninitialised', sampleRate: this.ctx?.sampleRate ?? 0, baseLatencyMs: this.ctx ? +((this.ctx.baseLatency ?? 0) * 1000).toFixed(1) : 0, outputLatencyMs: this.ctx && 'outputLatency' in this.ctx ? +(((this.ctx as AudioContext).outputLatency ?? 0) * 1000).toFixed(1) : null, epoch: this.epoch, timers: this.timers.size, gameMuted: isGameAudioMuted() },
      voices: { real: this.voices.size, cap: this.allocator.cap, peak: this.allocator.stats.peak, byBus, spatial, hrtf, hrtfCap: this.budget.hrtf, blocked, loops: this.loops.size, loopsLive, started: this.allocator.stats.started, stolen: this.allocator.stats.stolen, dropped: { ...this.allocator.stats.dropped } },
      mix: { volumes: { ...this.volumes }, muted: this.muted, output: this.output, nightMode: this.nightMode, ducks: [...this.ducks.keys()], duckGains: { ...this.duckApplied }, guardMakeupDb: this.guardMakeup > 0 ? +(20 * Math.log10(this.guardMakeup)).toFixed(2) : null, guardReductionDb: this.graph ? +this.graph.guard.reduction.toFixed(1) : 0 },
      listener: { x: +this.pose.x.toFixed(2), y: +this.pose.y.toFixed(2), z: +this.pose.z.toFixed(2), q: this.pose.q.map((n) => +n.toFixed(3)) },
      world: { env: { ...this.env }, reverb: this.reverbKind, reverbEnabled: this.reverbEnabled, convolverLive: !!this.graph?.convolver },
      assets: this.store.diag(),
      variants: Object.fromEntries(this.variants),
      work: { rays: this.stats.rays, raysBlocked: this.stats.raysBlocked, rayTokens: +this.rayTokens.toFixed(1), raysPerSec: this.budget.raysPerSec },
      stats: { ...this.stats },
    };
  }
}

interface RuntimeHolder { __atlasAudioRuntime?: AtlasAudioRuntime }
/** THE runtime. globalThis-backed: Vite's dev graph can serve one module under two URLs, and two runtimes would be two contexts again. */
export function audioRuntime(): AtlasAudioRuntime {
  const g = globalThis as unknown as RuntimeHolder;
  return (g.__atlasAudioRuntime ??= new AtlasAudioRuntime());
}

// Every gesture unlocks / resumes. NOT `{ once: true }`: the same listener is the recovery path after an
// interruption (iOS leaving the page, a suspended background tab) — cheap when the context is already running.
if (typeof window !== 'undefined') {
  const g = globalThis as unknown as { __atlasAudioUnlockBound?: boolean };
  if (!g.__atlasAudioUnlockBound) {
    g.__atlasAudioUnlockBound = true;
    for (const ev of ['pointerdown', 'keydown', 'touchend'] as const) window.addEventListener(ev, () => audioRuntime().unlock(), { capture: true, passive: true });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') { const rt = audioRuntime(); if (rt.ctx && rt.ctx.state !== 'running') rt.unlock(); } });
    (window as unknown as { __audio?: () => unknown }).__audio = () => audioRuntime().diag();
  }
}
